import {
  FC,
  MouseEvent,
  ReactElement,
  useCallback,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  FilterList,
  TuneRounded as FilterSortIcon,
  Sort,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import { DateRangeFilter, DialogMenu } from "@/components";
import TextFilterWithOperatorInput from "./TextFilterWithOperatorInput";
import RangeFilter from "./RangeFilter";
import MultiSelectFilter from "./MultiSelectFilter";
import { tokens } from "@/tokens";

// Filter operators for text fields
export type FilterOperator =
  | "contains"
  | "equals"
  | "starts_with"
  | "ends_with"
  | "not_contains"
  | "not_equals";

export interface TextFilterWithOperator {
  value: string;
  operator: FilterOperator;
}

// Sort configuration
export interface SortConfig {
  field: string;
  direction: "asc" | "desc";
}

export interface EntrySummaryFiltersState {
  // Date filters
  startDate?: Date;
  endDate?: Date;
  // Entry-level filters
  portOfEntry: string[];
  status: "" | "APPROVED" | "NEEDS_REVIEW" | "DISCREPANCIES_FOUND";
  // Line-level filters with operators
  countryOfOrigin: string[];
  htsCode: string[];
  manufacturerID: string[];
  filerCode: TextFilterWithOperator;
  commercialDescription: TextFilterWithOperator;
  entrySummaryNumber: TextFilterWithOperator;
  // Liquidation status filter
  liquidationStatus: string[];
  // Value/Duty range filters
  valueMin: string;
  valueMax: string;
  dutyMin: string;
  dutyMax: string;
  mfnDutyMin: string;
  mfnDutyMax: string;
  duty201Min: string;
  duty201Max: string;
  duty232Min: string;
  duty232Max: string;
  duty301Min: string;
  duty301Max: string;
  // Sort configuration
  sort: SortConfig;
}

export const initialEntrySummaryFilters: EntrySummaryFiltersState = {
  startDate: undefined,
  endDate: undefined,
  portOfEntry: [],
  status: "",
  liquidationStatus: [],
  countryOfOrigin: [],
  htsCode: [],
  manufacturerID: [],
  filerCode: { value: "", operator: "contains" },
  commercialDescription: { value: "", operator: "contains" },
  entrySummaryNumber: { value: "", operator: "contains" },
  valueMin: "",
  valueMax: "",
  dutyMin: "",
  dutyMax: "",
  mfnDutyMin: "",
  mfnDutyMax: "",
  duty201Min: "",
  duty201Max: "",
  duty232Min: "",
  duty232Max: "",
  duty301Min: "",
  duty301Max: "",
  sort: { field: "entryDate", direction: "desc" },
};

// Sort field options
const sortFields = [
  { value: "entryDate", label: "Entry Date" },
  { value: "entrySummaryNumber", label: "Entry Number" },
  { value: "portOfEntryCode", label: "Port of Entry" },
  { value: "totalGoodsValue", label: "Total Value" },
  { value: "totalLineDuties", label: "Total Duties" },
  { value: "lineCount", label: "Line Count" },
];

interface EntrySummaryFiltersProps {
  filters: EntrySummaryFiltersState;
  onFiltersChange: (filters: EntrySummaryFiltersState) => void;
  availablePorts: string[];
  availableCountries: string[];
  availableManufacturers?: string[];
  availableFilerCodes?: string[];
  availableHtsCodes?: Array<{ value: string; label: string }>;
  availableLiquidationStatuses?: string[];
}

const EntrySummaryFilters: FC<EntrySummaryFiltersProps> = ({
  filters,
  onFiltersChange,
  availablePorts,
  availableCountries,
  availableManufacturers = [],
  availableFilerCodes: _availableFilerCodes = [],
  availableHtsCodes = [],
  availableLiquidationStatuses = [],
}): ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [localFilters, setLocalFilters] =
    useState<EntrySummaryFiltersState>(filters);
  const [activeTab, setActiveTab] = useState(0);
  const [htsDropdownOpen, setHtsDropdownOpen] = useState(false);

  const toOptions = useCallback(
    (options: string[]) => options.map((value) => ({ value, label: value })),
    []
  );

  const openFilterModal = useCallback(
    (event: MouseEvent<HTMLButtonElement>): void => {
      setLocalFilters(filters);
      setAnchorEl(event.currentTarget);
    },
    [filters]
  );

  const closeFilterModal = useCallback((): void => {
    setAnchorEl(null);
  }, []);

  const applyFilters = (): void => {
    onFiltersChange(localFilters);
    closeFilterModal();
  };

  const resetFilters = useCallback((): void => {
    setLocalFilters(initialEntrySummaryFilters);
  }, []);

  const appliedFilterCount = useMemo(() => {
    let count = 0;

    if (filters.startDate || filters.endDate) count++;
    if (filters.portOfEntry.length > 0) count++;
    if (filters.countryOfOrigin.length > 0) count++;
    if (filters.htsCode.length > 0) count++;
    if (filters.manufacturerID.length > 0) count++;
    if (filters.filerCode.value) count++;
    if (filters.commercialDescription.value) count++;
    if (filters.entrySummaryNumber.value) count++;
    if (filters.status) count++;
    if (filters.liquidationStatus.length > 0) count++;
    if (filters.valueMin || filters.valueMax) count++;
    if (filters.dutyMin || filters.dutyMax) count++;
    if (filters.mfnDutyMin || filters.mfnDutyMax) count++;
    if (filters.duty201Min || filters.duty201Max) count++;
    if (filters.duty232Min || filters.duty232Max) count++;
    if (filters.duty301Min || filters.duty301Max) count++;

    return count;
  }, [filters]);

  return (
    <>
      <Button
        variant="outlined"
        onClick={openFilterModal}
        id="entry-summary-filter-anchor"
        aria-controls={anchorEl ? "entry-summary-filters-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={anchorEl ? "true" : undefined}
        startIcon={<FilterSortIcon sx={{ fontSize: 18 }} />}
        sx={{
          borderColor: tokens.border.default,
          color: tokens.text.body,
          bgcolor: tokens.bg.surface,
          '&:hover': { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
          borderRadius: tokens.radius.md,
          fontWeight: tokens.fontWeight.semibold,
          fontSize: tokens.fontSize.md,
          height: 36,
          px: 2,
        }}
      >
        Filter & Sort
        {appliedFilterCount > 0 && (
          <Box
            sx={{
              ml: 0.75,
              width: 20,
              height: 20,
              bgcolor: 'rgba(11,73,75,0.12)',
              display: 'grid',
              placeContent: 'center',
              borderRadius: '50%',
              fontSize: tokens.fontSize.xs,
              fontWeight: tokens.fontWeight.bold,
              color: tokens.brand.primary,
            }}
          >
            {appliedFilterCount}
          </Box>
        )}
      </Button>
      <DialogMenu
        id="entry-summary-filters-menu"
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={closeFilterModal}
        sx={{
          "& .MuiPaper-root": {
            width: 580,
            maxHeight: "85vh",
            borderRadius: "12px",
            boxShadow: "0px 20px 40px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          px={3}
          py={2}
          sx={{ borderBottom: `1px solid ${tokens.border.default}` }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: tokens.radius.md,
                bgcolor: `${tokens.brand.primary}0F`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FilterList sx={{ fontSize: 20, color: tokens.brand.primary }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                fontSize={16}
                fontWeight={tokens.fontWeight.bold}
                color={tokens.text.primary}
              >
                Filter & Sort
              </Typography>
              <Typography variant="caption" color={tokens.text.secondary}>
                Refine your entry data
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="text"
            size="small"
            onClick={resetFilters}
            sx={{
              color: tokens.brand.primary,
              fontWeight: tokens.fontWeight.semibold,
              fontSize: tokens.fontSize.base,
              "&:hover": {
                backgroundColor: tokens.bg.hover,
              },
            }}
          >
            Clear All
          </Button>
        </Stack>

        {/* Tabs */}
        <Box sx={{ borderBottom: `1px solid ${tokens.border.default}` }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              minHeight: 44,
              px: 2,
              "& .MuiTab-root": {
                minHeight: 44,
                textTransform: "none",
                fontWeight: tokens.fontWeight.semibold,
                fontSize: tokens.fontSize.base,
                color: tokens.text.secondary,
                "&.Mui-selected": {
                  color: tokens.brand.primary,
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: tokens.brand.primary,
                height: 2,
              },
            }}
          >
            <Tab
              icon={<FilterList sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Filters"
            />
            <Tab
              icon={<Sort sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Sort"
            />
          </Tabs>
        </Box>

        {/* Content */}
        <Box sx={{ maxHeight: "55vh", overflow: "auto" }}>
          {/* Sort Tab */}
          {activeTab === 1 && (
            <Stack spacing={3} p={3}>
              <Box>
                <Typography
                  sx={{
                    mb: 2,
                    color: tokens.text.primary,
                    fontWeight: tokens.fontWeight.bold,
                    fontSize: tokens.fontSize.base,
                  }}
                >
                  Sort By
                </Typography>
                <Stack spacing={1.5}>
                  {sortFields.map((field) => (
                    <Box
                      key={field.value}
                      onClick={() =>
                        setLocalFilters((prev) => ({
                          ...prev,
                          sort: { ...prev.sort, field: field.value },
                        }))
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1.5,
                        borderRadius: tokens.radius.md,
                        border: "1px solid",
                        borderColor:
                          localFilters.sort.field === field.value
                            ? tokens.brand.primary
                            : tokens.border.default,
                        backgroundColor:
                          localFilters.sort.field === field.value
                            ? `${tokens.brand.primary}0F`
                            : tokens.bg.surface,
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        "&:hover": {
                          borderColor: tokens.brand.primary,
                          backgroundColor: tokens.bg.hover,
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: tokens.fontSize.base,
                          fontWeight:
                            localFilters.sort.field === field.value ? tokens.fontWeight.bold : tokens.fontWeight.normal,
                          color:
                            localFilters.sort.field === field.value
                              ? tokens.brand.primary
                              : tokens.text.primary,
                        }}
                      >
                        {field.label}
                      </Typography>
                      {localFilters.sort.field === field.value && (
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            bgcolor: tokens.brand.primary,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: tokens.bg.surface,
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography
                  sx={{
                    mb: 2,
                    color: tokens.text.primary,
                    fontWeight: tokens.fontWeight.bold,
                    fontSize: tokens.fontSize.base,
                  }}
                >
                  Direction
                </Typography>
                <ToggleButtonGroup
                  value={localFilters.sort.direction}
                  exclusive
                  onChange={(_, newDirection) => {
                    if (newDirection) {
                      setLocalFilters((prev) => ({
                        ...prev,
                        sort: { ...prev.sort, direction: newDirection },
                      }));
                    }
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiToggleButton-root": {
                      flex: 1,
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: tokens.fontWeight.semibold,
                      fontSize: tokens.fontSize.base,
                      border: `1px solid ${tokens.border.default}`,
                      color: tokens.text.secondary,
                      "&.Mui-selected": {
                        backgroundColor: tokens.brand.primary,
                        color: tokens.bg.surface,
                        borderColor: tokens.brand.primary,
                        "&:hover": {
                          backgroundColor: tokens.brand.primaryHover,
                        },
                      },
                      "&:hover": {
                        backgroundColor: tokens.bg.hover,
                      },
                    },
                  }}
                >
                  <ToggleButton value="asc">
                    <ArrowUpward sx={{ fontSize: 18, mr: 1 }} />
                    Ascending
                  </ToggleButton>
                  <ToggleButton value="desc">
                    <ArrowDownward sx={{ fontSize: 18, mr: 1 }} />
                    Descending
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Stack>
          )}

          {/* Filters Tab */}
          {activeTab === 0 && (
            <Stack spacing={0}>
              {/* Entry Filters Section */}
              <Box sx={{ p: 3, borderBottom: `1px solid ${tokens.border.light}` }}>
                <Typography
                  sx={{
                    mb: 2.5,
                    color: tokens.text.primary,
                    fontWeight: tokens.fontWeight.bold,
                    fontSize: tokens.fontSize.base,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: tokens.brand.primary,
                    }}
                  />
                  Entry Filters
                </Typography>
                <Stack spacing={2.5}>
                  {/* Date Range */}
                  <Box>
                    <Typography
                      sx={{
                        mb: 1,
                        color: tokens.text.primary,
                        fontWeight: tokens.fontWeight.semibold,
                        fontSize: tokens.fontSize.base,
                      }}
                    >
                      Date Range
                    </Typography>
                    <DateRangeFilter
                      value={{
                        startDate: localFilters.startDate,
                        endDate: localFilters.endDate,
                      }}
                      onSelectDate={({ startDate, endDate }) =>
                        setLocalFilters((prev) => ({
                          ...prev,
                          startDate,
                          endDate,
                        }))
                      }
                    />
                  </Box>

                  {/* Entry Number */}
                  <TextFilterWithOperatorInput
                    label="Entry Number"
                    filterKey="entrySummaryNumber"
                    placeholder="Search entry number..."
                    localFilters={localFilters}
                    setLocalFilters={setLocalFilters}
                  />

                  {/* Port of Entry */}
                  <MultiSelectFilter
                    label="Port of Entry"
                    options={toOptions(availablePorts)}
                    value={localFilters.portOfEntry}
                    onChange={(newValue) =>
                      setLocalFilters((prev) => ({
                        ...prev,
                        portOfEntry: newValue,
                      }))
                    }
                    placeholder="Select ports..."
                  />

                  {/* Status */}
                  <Box>
                    <Typography
                      sx={{
                        mb: 1,
                        color: tokens.text.primary,
                        fontWeight: tokens.fontWeight.semibold,
                        fontSize: tokens.fontSize.base,
                      }}
                    >
                      Audit Status
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={localFilters.status}
                        onChange={(e) =>
                          setLocalFilters((prev) => ({
                            ...prev,
                            status: e.target
                              .value as EntrySummaryFiltersState["status"],
                          }))
                        }
                        displayEmpty
                        sx={{
                          backgroundColor: tokens.bg.surface,
                          "& .MuiSelect-select": {
                            fontSize: tokens.fontSize.base,
                          },
                        }}
                      >
                        <MenuItem value="" sx={{ fontSize: tokens.fontSize.base }}>
                          <em>All Statuses</em>
                        </MenuItem>
                        <MenuItem value="APPROVED" sx={{ fontSize: tokens.fontSize.base }}>
                          Approved
                        </MenuItem>
                        <MenuItem value="NEEDS_REVIEW" sx={{ fontSize: tokens.fontSize.base }}>
                          Needs Review
                        </MenuItem>
                        <MenuItem
                          value="DISCREPANCIES_FOUND"
                          sx={{ fontSize: tokens.fontSize.base }}
                        >
                          Discrepancies Found
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Liquidation Status */}
                  {availableLiquidationStatuses.length > 0 && (
                    <MultiSelectFilter
                      label="Liquidation Status"
                      options={toOptions(availableLiquidationStatuses)}
                      value={localFilters.liquidationStatus}
                      onChange={(newValue) =>
                        setLocalFilters((prev) => ({
                          ...prev,
                          liquidationStatus: newValue,
                        }))
                      }
                      placeholder="Select statuses..."
                    />
                  )}
                </Stack>
              </Box>

              {/* Line Filters Section */}
              <Box sx={{ p: 3, borderBottom: `1px solid ${tokens.border.light}` }}>
                <Typography
                  sx={{
                    mb: 2.5,
                    color: tokens.text.primary,
                    fontWeight: tokens.fontWeight.bold,
                    fontSize: tokens.fontSize.base,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: tokens.brand.primary,
                    }}
                  />
                  Line Filters
                </Typography>
                <Stack spacing={2.5}>
                  {/* Country of Origin */}
                  <MultiSelectFilter
                    label="Country of Origin"
                    options={toOptions(availableCountries)}
                    value={localFilters.countryOfOrigin}
                    onChange={(newValue) =>
                      setLocalFilters((prev) => ({
                        ...prev,
                        countryOfOrigin: newValue,
                      }))
                    }
                    placeholder="Select countries..."
                  />

                  {/* HTS Code */}
                  <MultiSelectFilter
                    label="HTS Code"
                    options={availableHtsCodes}
                    value={localFilters.htsCode}
                    onChange={(newValue) =>
                      setLocalFilters((prev) => ({
                        ...prev,
                        htsCode: newValue,
                      }))
                    }
                    placeholder="Select HTS codes..."
                    disableCloseOnSelect
                    disablePortal
                    open={htsDropdownOpen}
                    onOpen={() => setHtsDropdownOpen(true)}
                    onClose={(_, reason) => {
                      if (reason === "selectOption") return;
                      setHtsDropdownOpen(false);
                    }}
                  />

                  {/* Manufacturer ID */}
                  <MultiSelectFilter
                    label="Manufacturer ID (MID)"
                    options={toOptions(availableManufacturers)}
                    value={localFilters.manufacturerID}
                    onChange={(newValue) =>
                      setLocalFilters((prev) => ({
                        ...prev,
                        manufacturerID: newValue,
                      }))
                    }
                    placeholder="Select manufacturers..."
                  />

                  {/* Filer Code */}
                  <TextFilterWithOperatorInput
                    label="Filer Code"
                    filterKey="filerCode"
                    placeholder="Search filer code..."
                    localFilters={localFilters}
                    setLocalFilters={setLocalFilters}
                  />

                  {/* Commercial Description */}
                  <TextFilterWithOperatorInput
                    label="Commercial Description"
                    filterKey="commercialDescription"
                    placeholder="Search description..."
                    localFilters={localFilters}
                    setLocalFilters={setLocalFilters}
                  />
                </Stack>
              </Box>

              {/* Value & Duty Filters Section */}
              <Box sx={{ p: 3 }}>
                <Typography
                  sx={{
                    mb: 2.5,
                    color: tokens.text.primary,
                    fontWeight: tokens.fontWeight.bold,
                    fontSize: tokens.fontSize.base,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "#0369A1",
                    }}
                  />
                  Value & Duty Filters
                </Typography>
                <Stack spacing={2.5}>
                  <RangeFilter
                    label="Goods Value Range"
                    minKey="valueMin"
                    maxKey="valueMax"
                    localFilters={localFilters}
                    setLocalFilters={setLocalFilters}
                  />
                  <RangeFilter
                    label="Total Duty Range"
                    minKey="dutyMin"
                    maxKey="dutyMax"
                    localFilters={localFilters}
                    setLocalFilters={setLocalFilters}
                  />
                  <RangeFilter
                    label="MFN Duty Range"
                    minKey="mfnDutyMin"
                    maxKey="mfnDutyMax"
                    localFilters={localFilters}
                    setLocalFilters={setLocalFilters}
                  />
                  <RangeFilter
                    label="Section 201 Duty Range"
                    minKey="duty201Min"
                    maxKey="duty201Max"
                    localFilters={localFilters}
                    setLocalFilters={setLocalFilters}
                  />
                  <RangeFilter
                    label="Section 232 Duty Range"
                    minKey="duty232Min"
                    maxKey="duty232Max"
                    localFilters={localFilters}
                    setLocalFilters={setLocalFilters}
                  />
                  <RangeFilter
                    label="Section 301 Duty Range"
                    minKey="duty301Min"
                    maxKey="duty301Max"
                    localFilters={localFilters}
                    setLocalFilters={setLocalFilters}
                  />
                </Stack>
              </Box>
            </Stack>
          )}
        </Box>

        {/* Footer */}
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={2}
          px={3}
          py={2}
          sx={{
            borderTop: `1px solid ${tokens.border.default}`,
            background: tokens.bg.muted,
          }}
        >
          <Button
            variant="outlined"
            sx={{
              flex: 1,
              borderColor: tokens.border.default,
              color: tokens.text.primary,
              fontWeight: tokens.fontWeight.semibold,
              borderRadius: tokens.radius.md,
              height: 34,
              fontSize: tokens.fontSize.md,
              boxShadow: "none",
              "&:hover": {
                borderColor: tokens.text.disabled,
                backgroundColor: tokens.bg.surface,
              },
            }}
            onClick={closeFilterModal}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              flex: 1,
              bgcolor: tokens.brand.primary,
              fontWeight: tokens.fontWeight.semibold,
              borderRadius: tokens.radius.md,
              height: 34,
              fontSize: tokens.fontSize.md,
              boxShadow: "none",
              "&:hover": {
                bgcolor: tokens.brand.primaryHover,
              },
            }}
            onClick={applyFilters}
          >
            Apply
          </Button>
        </Stack>
      </DialogMenu>
    </>
  );
};

export default EntrySummaryFilters;
